import { useMemo } from 'react';
import FormFieldContainer from '../../../../reactForm/fields/FormFieldContainer';
import {
    FieldArrayField,
    type FieldArrayItemFieldSettings,
} from '../../../../reactForm/fields/FieldArrayField';
import type { ElementSettingsFormField } from '../../elements/types';
import { getRowsFieldItemFields } from '../settingsFormSchemaRuntime/shared';
import { createNormalizedDefaultRecord } from '../settingsFormSchemaRuntime/normalization';

interface RowsFieldOptions {
    name: string;
    settings: RowsFieldSettings;
}

interface RowsFieldSettings extends ElementSettingsFormField {
    type: 'rows';
    fields: readonly ElementSettingsFormField[];
    maxRows: number;
}

function createEmptyRow(rowFields: readonly ElementSettingsFormField[]): Record<string, unknown> {
    return createNormalizedDefaultRecord(rowFields, {});
}

function toFieldArrayItemFieldSettings(
    field: ElementSettingsFormField
): FieldArrayItemFieldSettings {
    const { name, label, type, helpText, readOnly, ...rest } = field;

    return {
        name,
        label,
        type,
        helpText,
        readOnly,
        ...rest,
    };
}

export default function RowsField({
    fieldOptions,
}: {
    fieldOptions: RowsFieldOptions;
}): JSX.Element {
    const { name, settings } = fieldOptions;
    const rowsFieldSettings = settings;
    const { maxRows } = rowsFieldSettings;
    const disabled = rowsFieldSettings.readOnly === true;
    const rowFields = useMemo(() => getRowsFieldItemFields(rowsFieldSettings), [rowsFieldSettings]);
    const itemFields = useMemo(
        () => rowFields.map((rowField) => toFieldArrayItemFieldSettings(rowField)),
        [rowFields]
    );
    const emptyRow = useMemo(() => createEmptyRow(rowFields), [rowFields]);

    return (
        <FormFieldContainer fieldOptions={fieldOptions} showOverrideCheckbox={false} inline={false}>
            <FieldArrayField
                name={name}
                itemFields={itemFields}
                maxRows={maxRows}
                disabled={disabled}
                createEmptyItem={() => ({ ...emptyRow })}
            />
        </FormFieldContainer>
    );
}
